import * as React from 'react';
import { useState, useContext } from 'react';
import { Typography, TextField, InputAdornment, useTheme, Button, Box} from '@mui/material';
import Grid from '@mui/material/Grid2';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';

import { UserContext } from '../store/userContext';
import UserForm from './UserForm';
import AddSitesDialog from './AddSitesDialog';

interface UserHeaderProps {
  userSearchTerm: string;
  setUserSearchTerm: (value: string) => void;
  siteSearchTerm: string;
  setSiteSearchTerm: (value: string) => void;
  selectedUser?: User;
}

const UserHeader: React.FC<UserHeaderProps> = ({
  userSearchTerm,
  setUserSearchTerm,
  siteSearchTerm,
  setSiteSearchTerm,
  selectedUser,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { addUser, addUserLoading, editUser, editUserLoading, getUsers } = useContext(UserContext);

  const [addUserOpen, setAddUserOpen] = useState(false);
  const [addSiteOpen, setAddSiteOpen] = useState(false);

  const handleAddUserSubmit = (data: UserFormDto) => {
    addUser(data, () => {
      setAddUserOpen(false);
      getUsers(true);
    });
  };

  const handleAddSiteSubmit = (selectedSiteIds: number[]) => {
    if (selectedUser) {
      const updatedUser: UserFormDto = {
        name: selectedUser.name,
        email: selectedUser.email,
        role: selectedUser.role,
        locale: selectedUser.locale,
        siteIds: selectedSiteIds,
      };
      editUser(selectedUser.id, updatedUser, () => {
        setAddSiteOpen(false);
        getUsers(true);
      });
    }
  };

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Grid
            container
            alignItems="center"
            sx={{
                backgroundColor: theme.palette.headerBackground,
                px: 2,
                height: 50,
                borderBottom: theme.palette.border,
            }}
        >
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ marginRight: theme.spacing(1) }}
            >
                <path d="M4 21V9L12 3L20 9V21H14V14H10V21H4Z" fill="#1F9122" />
            </svg>
            <Typography sx={{ mr: 1, ...theme.typography.usersFontTitle }}>/</Typography>
            {selectedUser && (
              <>
                <Typography
                  variant="h6"
                  sx={{
                    ...theme.typography.usersFontTitle,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '300px', // Added a maxWidth to ensure truncation happens
                  }}
                >
                  {selectedUser.email}
                </Typography>
              </>
            )}
        </Grid>
      <Grid
        container
        alignItems="stretch"
        sx={{
          backgroundColor: theme.palette.headerBackground,
          height: 50,
          borderBottom: theme.palette.border,
        }}
      >
        <Grid size={{ xs: 3 }} sx={{ borderRight: theme.palette.border, display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
          <Typography variant="subtitle2" sx={(theme) => ({ ...theme.typography.columnHeader })}>
            {t('users:t.users')}
          </Typography>
          <Button
            sx={{ minWidth: 'auto', p: 0, color: 'green', height: 24 }}
            onClick={() => setAddUserOpen(true)}
          >
            <AddIcon />
          </Button>
        </Grid>
        <Grid size={{ xs: 3 }} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
          <Typography variant="subtitle2" sx={{ ...theme.typography.columnHeader }}>
            {t('sites:t.siteAccess')}
          </Typography>
          <Button
            sx={{ minWidth: 'auto', p: 0, color: 'green', height: 24 }}
            disabled={!selectedUser}
            onClick={() => setAddSiteOpen(true)}
          >
            <AddIcon />
          </Button>
        </Grid>
        <Grid size={{ xs: 6 }} sx={{ borderLeft: theme.palette.border, display: 'flex', alignItems: 'center', p: 2 }}>
          <Typography variant="subtitle2" sx={{ ...theme.typography.columnHeader }}>
            {t('reports:t.subscribedReports')}
          </Typography>
        </Grid>
      </Grid>
      <Grid container alignItems="stretch" sx={{ borderBottom: theme.palette.border }}>
        <Grid size={{ xs: 3 }} sx={{ borderRight: theme.palette.border, p: 1 }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder={'Find user...'}
            value={userSearchTerm}
            onChange={(e) => setUserSearchTerm(e.target.value)}
            sx={{
              mt: 0.5,
              width: '100%',
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.87)',
                },
              },
              '& .MuiInputBase-input': {
                ...theme.typography.usersFontRegular,
                fontSize: '0.8rem',
                py: 0.5,
              },
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon sx={{ fontSize: '1rem', color: 'green' }} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 3 }} sx={{ p: 1 }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder={'Find site...'}
            value={siteSearchTerm}
            onChange={(e) => setSiteSearchTerm(e.target.value)}
            sx={{
              mt: 0.5,
              width: '100%',
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.87)',
                },
              },
              '& .MuiInputBase-input': {
                ...theme.typography.usersFontRegular,
                fontSize: '0.8rem',
                py: 0.5,
              },
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon sx={{ fontSize: '1rem', color: 'green' }} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 6 }} sx={{ borderLeft: theme.palette.border, minHeight: '32px', display: 'flex' }}>
            <Grid size={{ xs: 6 }} sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                <Typography variant="subtitle2" sx={{ ...theme.typography.columnHeader }}>
                    Storages
                </Typography>
            </Grid>
            <Grid size={{ xs: 6 }} sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                <Typography variant="subtitle2" sx={{ ...theme.typography.columnHeader }}>
                    Report type
                </Typography>
            </Grid>
        </Grid>
      </Grid>
    </Grid>
    <UserForm
      open={addUserOpen}
      onCancel={() => setAddUserOpen(false)}
      onSubmit={handleAddUserSubmit}
      buttonText={t('common:t.add')}
      loading={addUserLoading}
      cancelButton
    />
    {selectedUser && (
      <AddSitesDialog
        open={addSiteOpen}
        onClose={() => setAddSiteOpen(false)}
        onSubmit={handleAddSiteSubmit}
        currentSiteIds={selectedUser.siteIds || []}
        loading={editUserLoading}
      />
    )}
    </>
  );
};

export default UserHeader;
